import styled from "styled-components";
import { InputWrapper } from "common/components/Wrapper/InputBase";
import { useValidCheckSingle } from "common/hooks/validCheck";
import { HiddenCheckbox } from "./FormInputs";
import { IconWrapper, FixedIcon } from "common/components/icon";
import { colors } from "common/const/colors";
import { useEffect } from "react";

const buttonSizes = {
  small: {
    button: "1.5rem",
    circle: "0.5rem",
    fixedIcon: { width: "0.5rem", height: "3px" },
  },
  medium: {
    button: "1.75rem",
    circle: "0.625rem",
    fixedIcon: { width: "0.5rem", height: "2px" },
  },
  large: {
    button: "2.75rem",
    circle: "1.125rem",
    fixedIcon: { width: "0.75rem", height: "3px" },
  },
};

const ToggleWrapper = styled.div`
  position: relative;
  width: ${({ size }) => buttonSizes[size].button};
  height: ${({ size }) => `calc(${buttonSizes[size].button} / 2)`};
  background-color: ${({ $checked }) =>
    $checked ? colors.MainBlue : colors.Gray};
  border-radius: 999px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;

const ToggleThumb = styled.span`
  position: absolute;
  top: 2px;
  left: ${({ $checked, size }) => getThumbLeft($checked, size)};
  width: ${({ size }) => buttonSizes[size].circle};
  aspect-ratio: 1;
  background-color: white;
  border-radius: 50%;
  transition: left 0.2s;
`;

function getThumbLeft(checked, size) {
  if (!checked) return "2px";
  const btnSize = buttonSizes[size].button;
  const circleSize = buttonSizes[size].circle;
  return `calc(${btnSize} - ${circleSize} - 2px)`;
}

export const Toggle = ({
  //InputWrapper
  label,
  caption,

  //Toggle
  name,
  checked,
  onChange,
  size = "medium",
  required = false,
  fixed = false,
  disabled = false,
  defaultValue = false,
}) => {
  const isControlled = typeof checked === "boolean";
  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name: name,
    rules: { required: required || false },
    defaultValue: isControlled ? checked : defaultValue,
  });

  useEffect(() => {
    if (isControlled && checked !== inputProps.value) {
      onValueChange(checked);
    }
  }, [checked, isControlled]);

  const currentValue = Boolean(
    isControlled ? checked : (inputProps.value ?? defaultValue)
  );

  const handleToggle = () => {
    if (disabled || fixed) return;
    const newValue = !currentValue;
    isControlled ? onChange?.(newValue) : onValueChange(newValue);
  };

  return (
    <InputWrapper
      label={label}
      caption={caption}
      rules={{ required }}
      error={error}>
      <ToggleWrapper size={size} $checked={currentValue} onClick={handleToggle}>
        <HiddenCheckbox
          name={inputProps.name}
          checked={currentValue}
          readOnly
        />
        {fixed ? (
          <IconWrapper size="100%" style={{ display: "flex" }}>
            <FixedIcon
              style={{
                width: buttonSizes[size].fixedIcon.width,
              }}
              strokeWidth={buttonSizes[size].fixedIcon.height}
              color={colors.White}
            />
          </IconWrapper>
        ) : (
          <ToggleThumb size={size} $checked={currentValue} />
        )}
      </ToggleWrapper>
    </InputWrapper>
  );
};
